// Libs
import React, { useState, useContext } from "react";
import * as Mui from "@material-ui/core";
import { Typography, useMediaQuery } from "@material-ui/core";

// App
import { StoresContext, Stores } from "Globals/Stores";

// Models

// ViewModels
import ApplicationFormViewModel from "Globals/ViewModels/ApplicationFormViewModel";
import ApplicantViewModel from "Globals/ViewModels/ApplicantViewModel";

// Views
import { CustomYesNoRadio } from "Globals/Views/CustomYesNoRadio";
import { CustomDatePicker } from "Globals/Views/CustomDatePicker";

// Styles
import { Row } from "Globals/Styles/LayoutStyles";
import { CustomInput } from "Globals/Styles/AppStyling";
import BackgroundInformationViewModel from "Globals/ViewModels/FormSections/BackgroundInformation/BackgroundInformationViewModel";
import { observer } from "mobx-react-lite";
import { KeyValuePair } from "@shoothill/core";
import { BackgroundInformationDisabilityImpact } from "Globals/Models/Domain/FormSections/BackgroundInformation/BackgroundInformationDisabilityImpact";
import { DisabilityImpactModel } from "Shared";

interface IProps {
    applicant: ApplicantViewModel;
    applicationFormViewModel: ApplicationFormViewModel;
}

export const BackgroundInformationView: React.FC<IProps> = observer((props: IProps) => {
    const storesContext = useContext<Stores>(StoresContext);
    const [viewModel] = useState(props.applicant.backgroundInformationViewModel as BackgroundInformationViewModel);
    const isMobile: boolean = useMediaQuery("screen and (max-width: 767px)") ? true : false;

    return (
        <div onKeyUp={() => viewModel.restartTimer()}>
            {viewModel.DevTool_ShowCanProceed && <h5 style={{ color: "red" }}>Can Proceed: {viewModel.canProceed ? "Yes" : "No"}</h5>}
            <CitizenshipSection viewModel={viewModel} applicationFormViewModel={props.applicationFormViewModel} />
            <VisaSection viewModel={viewModel} applicationFormViewModel={props.applicationFormViewModel} />
            <EqualOpportunitiesHeader />
            <EqualOpportunitiesDescription />
            <AgeSection viewModel={viewModel} storesContext={storesContext} applicationFormViewModel={props.applicationFormViewModel} isMobile={isMobile} />
            <GenderSection viewModel={viewModel} storesContext={storesContext} applicationFormViewModel={props.applicationFormViewModel} isMobile={isMobile} />
            <EthnicitySection viewModel={viewModel} storesContext={storesContext} applicationFormViewModel={props.applicationFormViewModel} isMobile={isMobile} />
            <SexualOrientationSection viewModel={viewModel} storesContext={storesContext} applicationFormViewModel={props.applicationFormViewModel} isMobile={isMobile} />
            <ReligionSection viewModel={viewModel} storesContext={storesContext} applicationFormViewModel={props.applicationFormViewModel} isMobile={isMobile} />
            <DisabilitySection viewModel={viewModel} storesContext={storesContext} applicationFormViewModel={props.applicationFormViewModel} isMobile={isMobile} />
            <DisabilityImpactSection viewModel={viewModel} storesContext={storesContext} applicationFormViewModel={props.applicationFormViewModel} isMobile={isMobile} />
            <NationalitySection viewModel={viewModel} storesContext={storesContext} applicationFormViewModel={props.applicationFormViewModel} isMobile={isMobile} />
            <DevelopmentalConditionSection viewModel={viewModel} storesContext={storesContext} applicationFormViewModel={props.applicationFormViewModel} isMobile={isMobile} />
            <SupportNeedsSection viewModel={viewModel} storesContext={storesContext} applicationFormViewModel={props.applicationFormViewModel} isMobile={isMobile} />
            <LanguageBarrierSection viewModel={viewModel} storesContext={storesContext} applicationFormViewModel={props.applicationFormViewModel} isMobile={isMobile} />
            <SpokenLanguageSection viewModel={viewModel} storesContext={storesContext} applicationFormViewModel={props.applicationFormViewModel} isMobile={isMobile} />
        </div>
    );
});

// --- Local Typed React Components ---

interface SectionProps {
    viewModel: BackgroundInformationViewModel;
    applicationFormViewModel: ApplicationFormViewModel;
}
interface SectionWithStoreProps extends SectionProps {
    storesContext: Stores;
    isMobile: boolean;
}

const CitizenshipSection: React.FC<SectionProps> = observer(({ viewModel, applicationFormViewModel }) => (
    <Row>
        <CustomYesNoRadio
            label="Are you a British or EU / EEA citizen?"
            fieldName={"isBritishOrEUCitizen"}
            value={viewModel.model.isBritishOrEUCitizen}
            onChange={viewModel.handleRadioChange}
            disabled={applicationFormViewModel.model.isSubmitted}
            error={viewModel.getError("isBritishOrEUCitizen")}
        />
        {viewModel.model.isBritishOrEUCitizen === false && (
            <CustomYesNoRadio
                label="Is your passport stamped with indefinite leave to remain?"
                fieldName={"isPassportIndefinite"}
                value={viewModel.model.isPassportIndefinite}
                onChange={viewModel.handleRadioChange}
                disabled={applicationFormViewModel.model.isSubmitted}
                error={viewModel.getError("isPassportIndefinite")}
            />
        )}
    </Row>
));

const VisaSection: React.FC<SectionProps> = observer(({ viewModel, applicationFormViewModel }) => (
    <Row>
        {viewModel.model.isBritishOrEUCitizen === false && viewModel.model.isPassportIndefinite === false && (
            <CustomDatePicker
                viewModel={viewModel}
                label="When does your leave to remain / work visa end?"
                placeholder={"Enter date"}
                fieldName="visaEndDate"
                value={viewModel.model.visaEndDate}
                onChange={(date) => {
                    viewModel.setDate("visaEndDate", date);
                    viewModel.restartTimer();
                }}
                resetCallback={viewModel.restartTimer}
                disabled={applicationFormViewModel.model.isSubmitted}
                error={viewModel.getError("visaEndDate")}
            />
        )}
    </Row>
));

const EqualOpportunitiesHeader: React.FC = () => (
    <Row>
        <Typography variant="h6">Equal opportunities</Typography>
    </Row>
);

const EqualOpportunitiesDescription: React.FC = () => (
    <Row style={{ marginTop: "0px", padding: "0px" }}>
        <Typography variant="body1" style={{ fontSize: "14px" }}>
            The information in the equalities section is requested to enable Hightown to make sure we treat all applicants fairly. It is also used for statistical returns to
            Government agencies. All personal data provided is treated in confidence. Providing the information is optional and your application will not be affected if you choose
            not to provide the information. Please tick one box for each applicant in each section.
        </Typography>
    </Row>
);

const AgeSection: React.FC<SectionWithStoreProps> = observer(({ viewModel, storesContext, applicationFormViewModel, isMobile }) => (
    <Row>
        <CustomInput
            type="select"
            label="Age"
            style={isMobile ? { width: "100%" } : { minWidth: "350px" }}
            viewModel={viewModel}
            fieldName="ageId"
            selectItems={storesContext.Domain.AgeStore.getAgeOptions}
            inputProps={{
                disabled: applicationFormViewModel.model.isSubmitted,
            }}
        />
    </Row>
));

const GenderSection: React.FC<SectionWithStoreProps> = observer(({ viewModel, storesContext, applicationFormViewModel, isMobile }) => (
    <Row>
        <CustomInput
            type="select"
            style={isMobile ? { width: "100%" } : { minWidth: "350px" }}
            label="Gender"
            viewModel={viewModel}
            fieldName="gender"
            selectItems={storesContext.Domain.LookupStore.getGenderOptions}
            inputProps={{
                disabled: applicationFormViewModel.model.isSubmitted,
            }}
        />
    </Row>
));

const EthnicitySection: React.FC<SectionWithStoreProps> = observer(({ viewModel, storesContext, applicationFormViewModel, isMobile }) => (
    <Row>
        <CustomInput
            type="select"
            style={isMobile ? { width: "100%" } : { minWidth: "350px" }}
            label="Which of these best describes your ethnic background?"
            viewModel={viewModel}
            fieldName="ethnicityId"
            selectItems={storesContext.Domain.EthnicityStore.getEthnicityOptions(viewModel.model.ethnicityId)}
            inputProps={{
                disabled: applicationFormViewModel.model.isSubmitted,
            }}
        />
        {storesContext.Domain.EthnicityStore.getRequiresOther(viewModel.model.ethnicityId) === true && (
            <CustomInput
                label="Please state"
                placeHolder="Enter ethnicity"
                viewModel={viewModel}
                fieldName="ethnicityOther"
                shrink={true}
                onChange={() => viewModel.handleInputChange()}
                inputProps={{
                    disabled: applicationFormViewModel.model.isSubmitted,
                }}
            />
        )}
    </Row>
));

const SexualOrientationSection: React.FC<SectionWithStoreProps> = observer(({ viewModel, storesContext, applicationFormViewModel, isMobile }) => (
    <Row>
        <CustomInput
            type="select"
            style={isMobile ? { width: "100%" } : { minWidth: "350px" }}
            label="Sexual orientation"
            viewModel={viewModel}
            fieldName="sexualOrientationId"
            selectItems={storesContext.Domain.SexualOrientationStore.getSexualOrientationOptions}
            inputProps={{
                disabled: applicationFormViewModel.model.isSubmitted,
            }}
        />
    </Row>
));

const ReligionSection: React.FC<SectionWithStoreProps> = observer(({ viewModel, storesContext, applicationFormViewModel, isMobile }) => (
    <Row>
        <CustomInput
            type="select"
            style={isMobile ? { width: "100%" } : { minWidth: "350px" }}
            label="Religion"
            viewModel={viewModel}
            fieldName="religionId"
            selectItems={storesContext.Domain.ReligionStore.getReligionOptions}
            inputProps={{
                disabled: applicationFormViewModel.model.isSubmitted,
            }}
        />
    </Row>
));

const DisabilitySection: React.FC<SectionWithStoreProps> = observer(({ viewModel, storesContext, applicationFormViewModel, isMobile }) => {
    const selectedDisability = storesContext.Domain.DisabilityStore.getDisabilityOptions.find((d: any) => d.value === viewModel.getValue("disabilityId"));
    const showOther = selectedDisability && selectedDisability.key.toLocaleLowerCase().includes("other");
    return (
        <>
            <Row>
                <CustomInput
                    type="select"
                    style={isMobile ? { width: "100%" } : { minWidth: "350px" }}
                    label="Disability"
                    viewModel={viewModel}
                    fieldName="disabilityId"
                    selectItems={storesContext.Domain.DisabilityStore.getDisabilityOptions}
                    inputProps={{
                        disabled: applicationFormViewModel.model.isSubmitted,
                    }}
                />
                {showOther && (
                    <CustomInput
                        label="Please state"
                        placeHolder="Enter disability"
                        viewModel={viewModel}
                        fieldName="disabilityOther"
                        shrink={true}
                        onChange={() => viewModel.handleInputChange()}
                        inputProps={{
                            disabled: applicationFormViewModel.model.isSubmitted,
                        }}
                    />
                )}
                <CustomInput
                    type="select"
                    style={isMobile ? { width: "100%" } : { minWidth: "350px" }}
                    label="Special requirements"
                    viewModel={viewModel}
                    fieldName="specialRequirementsId"
                    selectItems={storesContext.Domain.SpecialRequirementsStore.getSpecialRequirementsOptions}
                    inputProps={{
                        disabled: applicationFormViewModel.model.isSubmitted,
                    }}
                />
            </Row>
        </>
    );
});

const DisabilityImpactSection: React.FC<SectionWithStoreProps> = observer(({ viewModel, storesContext, applicationFormViewModel, isMobile }) => {
    const selectedImpacts = viewModel.getValue("disabilityImpacts") as DisabilityImpactModel[] | undefined;
    const hasOtherSelected = Array.isArray(selectedImpacts) && selectedImpacts.some((di) => (di?.name ?? "").toLowerCase().includes("other"));

    return (
        <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
            <Row>
                <CustomInput
                    multiLine
                    fullwidth
                    type="multiselect"
                    style={isMobile ? { width: "100%" } : { width: "730px" }}
                    label="Disability impact"
                    viewModel={viewModel}
                    fieldName="disabilityImpacts"
                    selectItems={storesContext.Domain.LookupStore.getDisabilityImpactOptions}
                    inputProps={{
                        disabled: applicationFormViewModel.model.isSubmitted,
                        renderValue: (selected: DisabilityImpactModel[]) => (
                            <div style={{ display: "flex", flexWrap: "wrap", gap: "8px" }}>
                                {selected.map((value) => {
                                    const isLong = value.name.length > 60;
                                    const truncated = isLong ? `${value.name.slice(0, 60)}…` : value.name;
                                    const chip = <Mui.Chip key={value.id} label={truncated} size="small" style={{ width: "fit-content" }} />;
                                    return isLong ? (
                                        <Mui.Tooltip key={value.id} title={value.name} placement="top">
                                            <span>{chip}</span>
                                        </Mui.Tooltip>
                                    ) : (
                                        chip
                                    );
                                })}
                            </div>
                        ),
                    }}
                />
            </Row>
            {hasOtherSelected && (
                <Row>
                    <CustomInput
                        multiLine
                        fullwidth
                        label="Please provide details"
                        placeHolder="Enter disability impact details"
                        viewModel={viewModel}
                        fieldName="disabilityImpactOtherDetail"
                        shrink={true}
                        onChange={() => viewModel.handleInputChange()}
                        inputProps={{
                            disabled: applicationFormViewModel.model.isSubmitted,
                            rows: 3,
                        }}
                    />
                </Row>
            )}
        </div>
    );
});

const NationalitySection: React.FC<SectionWithStoreProps> = observer(({ viewModel, storesContext, applicationFormViewModel, isMobile }) => (
    <Row>
        <CustomInput
            type="select"
            style={isMobile ? { width: "100%" } : { minWidth: "350px" }}
            label="Nationality"
            viewModel={viewModel}
            fieldName="nationalityId"
            selectItems={storesContext.Domain.NationalityStore.getNationalityOptions}
            inputProps={{
                disabled: applicationFormViewModel.model.isSubmitted,
            }}
        />
    </Row>
));

const DevelopmentalConditionSection: React.FC<SectionWithStoreProps> = observer(({ viewModel, storesContext, applicationFormViewModel, isMobile }) => (
    <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
        <Row>
            <CustomInput
                type="select"
                style={isMobile ? { width: "100%" } : { minWidth: "350px" }}
                label="Do you have a physical, mental health or lifelong developmental condition (or other illness) expected to last 12 months or more?"
                viewModel={viewModel}
                fieldName="developmentalConditionId"
                selectItems={storesContext.Domain.LookupStore.getDevelopmentalConditionOptions}
                inputProps={{
                    disabled: applicationFormViewModel.model.isSubmitted,
                }}
            />
        </Row>
        {viewModel.showDevelopmentalConditionOtherInput && (
            <Row>
                <CustomInput
                    label="Please provide details"
                    placeHolder="Enter details"
                    viewModel={viewModel}
                    fieldName="developmentalConditionOther"
                    shrink={true}
                    onChange={() => viewModel.handleInputChange()}
                    inputProps={{
                        disabled: applicationFormViewModel.model.isSubmitted,
                    }}
                />
            </Row>
        )}
    </div>
));

const SupportNeedsSection: React.FC<SectionWithStoreProps> = observer(({ viewModel, storesContext, applicationFormViewModel, isMobile }) => (
    <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
        <Row>
            <CustomInput
                type="select"
                style={isMobile ? { width: "100%" } : { minWidth: "350px" }}
                label="Do you have any other support needs you'd like us to be aware of?"
                viewModel={viewModel}
                fieldName="supportNeedsId"
                selectItems={storesContext.Domain.LookupStore.getSupportNeedsOptions}
                inputProps={{
                    disabled: applicationFormViewModel.model.isSubmitted,
                }}
            />
        </Row>
        {viewModel.showSupportNeedsOtherInput && (
            <Row>
                <CustomInput
                    label="Please provide details"
                    placeHolder="Enter details"
                    viewModel={viewModel}
                    fieldName="supportNeedsOther"
                    shrink={true}
                    onChange={() => viewModel.handleInputChange()}
                    inputProps={{
                        disabled: applicationFormViewModel.model.isSubmitted,
                    }}
                />
            </Row>
        )}
    </div>
));

const LanguageBarrierSection: React.FC<SectionWithStoreProps> = observer(({ viewModel, storesContext, applicationFormViewModel, isMobile }) => (
    <Row>
        <CustomInput
            type="select"
            style={isMobile ? { width: "100%" } : { minWidth: "350px" }}
            label="How well do you speak English?"
            viewModel={viewModel}
            fieldName="languageBarrierId"
            selectItems={storesContext.Domain.LookupStore.getLanguageBarrierOptions}
            inputProps={{
                disabled: applicationFormViewModel.model.isSubmitted,
            }}
        />
    </Row>
));

const SpokenLanguageSection: React.FC<SectionWithStoreProps> = observer(({ viewModel, storesContext, applicationFormViewModel, isMobile }) => (
    <Row>
        <CustomInput
            type="select"
            style={isMobile ? { width: "100%" } : { minWidth: "350px" }}
            label="Spoken language"
            viewModel={viewModel}
            fieldName="spokenLanguageId"
            selectItems={storesContext.Domain.LookupStore.getSpokenLanguageOptions}
            inputProps={{
                disabled: applicationFormViewModel.model.isSubmitted,
            }}
        />
        {viewModel.showSpokenLanguageOtherInput && (
            <CustomInput
                label="Please state"
                placeHolder="Enter other spoken language"
                viewModel={viewModel}
                fieldName="spokenLanguageOther"
                shrink={true}
                onChange={() => viewModel.handleInputChange()}
                inputProps={{
                    disabled: applicationFormViewModel.model.isSubmitted,
                }}
            />
        )}
    </Row>
));
